import { useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSessao } from '../../auth/contexto'
import { cn } from '../../lib/cn'
import { TEXTOS } from '../../lib/glossario'
import { itemAtivo, itemVisivelPara, NAVEGACAO, type ItemDeNavegacao } from '../../lib/navegacao'
import { IconeFechar, IconeSeta } from '../Icones'

/** O `id` do menu. A BarraSuperior aponta o `aria-controls` do botão da gaveta para cá. */
export const ID_DO_MENU = 'menu-principal'

const FOCAVEIS = 'a[href], button:not([disabled])'

/** Grupos que aparecem sem cabeçalho: o Painel sozinho e a conta da pessoa no rodapé. */
const SEM_CABECALHO = new Set(['visao-geral', 'conta'])

interface Props {
  /** Gaveta aberta em tela pequena. Em tela grande o menu está sempre visível. */
  aberta: boolean
  /** Menu reduzido a ícones. Só vale em tela grande. */
  recolhida: boolean
  aoFechar: () => void
  aoAlternarRecolhida: () => void
}

/**
 * O menu lateral: fixo em tela grande, gaveta em tela pequena.
 *
 * Fechada, a gaveta fica `invisible` e não só deslocada para fora da tela. Esconder por
 * `translate` deixa os links focáveis: quem navega por teclado sai do primeiro campo e cai em
 * uma lista de links que não estão em lugar nenhum da tela, sem entender onde o foco foi parar.
 *
 * O item aceso sai de `itemAtivo`, e não do `isActive` do `NavLink`: `/gmuds` e `/gmuds/novo` são
 * dois itens, `/gmuds/7` não é nenhum dos dois e mesmo assim pertence a "Solicitações", e o
 * histórico é a rota da lista com um recorte. O `NavLink` acenderia dois itens ao mesmo tempo e
 * marcaria os dois com `aria-current`.
 *
 * @param props estado da gaveta, do recolhimento e os comandos de fechar e recolher
 * @returns o menu lateral
 */
export function BarraLateral({ aberta, recolhida, aoFechar, aoAlternarRecolhida }: Props) {
  const local = useLocation()
  const { usuario } = useSessao()
  const painel = useRef<HTMLDivElement>(null)
  const ativo = itemAtivo(local.pathname, local.search)

  /**
   * Enquanto a gaveta está aberta ela é modal: Esc fecha, Tab circula dentro dela e o foco volta
   * para o botão que a abriu. É o mesmo contrato do `Dialogo`, repetido aqui porque a função de
   * prender o foco não é exportada de lá — quando alguém puder mexer nos dois arquivos, junte.
   */
  useEffect(() => {
    if (!aberta) {
      return
    }
    const anterior = document.activeElement as HTMLElement | null
    painel.current?.querySelector<HTMLElement>(FOCAVEIS)?.focus()

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation()
        aoFechar()
      } else if (evento.key === 'Tab') {
        prenderFoco(evento, painel.current)
      }
    }
    document.addEventListener('keydown', aoTeclar, true)
    return () => {
      document.removeEventListener('keydown', aoTeclar, true)
      anterior?.focus()
    }
  }, [aberta, aoFechar])

  // Filtro de conveniência do menu, NÃO autorização: quem digitar a rota chega na tela e recebe a
  // recusa do servidor, que é onde a regra mora. Serve para não oferecer "Abrir solicitação" a
  // quem só consulta e descobriria isso depois de preencher o formulário inteiro — e, desde a
  // segregação da contratada, para não anunciar a fiscalização a quem é fiscalizado.
  const grupos = NAVEGACAO.map((grupo) => ({
    ...grupo,
    itens: grupo.itens.filter((item) => itemVisivelPara(item, usuario)),
  })).filter((grupo) => grupo.itens.length > 0)

  return (
    <>
      {aberta && <div aria-hidden="true" onClick={aoFechar} className="fixed inset-0 z-30 bg-niteroi-900/40 lg:hidden" />}

      <div
        ref={painel}
        role={aberta ? 'dialog' : undefined}
        aria-modal={aberta ? true : undefined}
        aria-label={aberta ? 'Menu principal' : undefined}
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-[17rem] flex-col border-r border-slate-200 bg-white',
          'transition-[transform,width] duration-200 lg:visible lg:translate-x-0',
          recolhida && 'lg:w-[4.5rem]',
          aberta ? 'visible translate-x-0 shadow-cartao-alto' : 'invisible -translate-x-full',
        )}
      >
        {/* A marca do sistema mora aqui, e não na barra superior: é o canto que não muda de tela
            para tela. Recolhido o menu, sobra o brasão — que já diz de quem é a casa. */}
        <div className="flex h-[3.75rem] shrink-0 items-center gap-3 border-b border-slate-200 px-4">
          <img src="/img/brasao-niteroi.svg" alt="" className="h-9 w-auto shrink-0" />
          <div className={cn('min-w-0 leading-tight', recolhida && 'lg:sr-only')}>
            <p className="truncate text-[15px] font-bold text-niteroi-700">{TEXTOS.nomeDoSistema}</p>
            <p className="truncate text-xs text-slate-500">Prefeitura de Niterói</p>
          </div>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar o menu"
            className="ml-auto rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 lg:hidden"
          >
            <IconeFechar className="h-5 w-5" />
          </button>
        </div>

        <nav id={ID_DO_MENU} aria-label="Navegação principal" className="flex flex-1 flex-col overflow-y-auto py-3 pr-3">
          {grupos.map((grupo) => (
            <div
              key={grupo.id}
              // A conta vai para o rodapé, separada por um filete e sem cabeçalho: não é um grupo de
              // telas, é a própria pessoa. O grupo de um item só (o Painel) também dispensa cabeçalho —
              // "VISÃO GERAL" em caixa alta anunciando uma linha é cerimônia.
              className={cn('mb-3 last:mb-0', grupo.id === 'conta' && 'mt-auto border-t border-slate-200 pt-3')}
            >
              {!SEM_CABECALHO.has(grupo.id) && (
                <p
                  id={`grupo-${grupo.id}`}
                  // Em caixa normal: o rótulo do grupo é lido, não soletrado. A caixa alta espaçada
                  // fazia "CONTRATO E FISCALIZAÇÃO" pesar mais do que os itens que ele agrupa.
                  className={cn('px-4 pb-1 pt-1 text-xs font-semibold text-slate-500', recolhida && 'lg:sr-only')}
                >
                  {grupo.rotulo}
                </p>
              )}
              <ul aria-label={SEM_CABECALHO.has(grupo.id) ? grupo.rotulo : undefined} aria-labelledby={SEM_CABECALHO.has(grupo.id) ? undefined : `grupo-${grupo.id}`} className="space-y-0.5">
                {grupo.itens.map((item) => (
                  <li key={item.id}>
                    <ItemDoMenu item={item} ativo={item.id === ativo?.id} recolhida={recolhida} aoNavegar={aoFechar} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Recolher fica no pé, longe da marca: no topo, o botão disputava os 72px do menu recolhido
            com o brasão e acabava cortado. Só existe em tela grande — na pequena o menu é gaveta. */}
        <div className="hidden shrink-0 border-t border-slate-200 p-2 lg:block">
          <button
            type="button"
            onClick={aoAlternarRecolhida}
            aria-label={recolhida ? 'Expandir o menu' : 'Recolher o menu'}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-slate-500',
              'hover:bg-slate-100 hover:text-slate-800',
              recolhida && 'justify-center',
            )}
          >
            <IconeSeta className={cn('h-4 w-4', !recolhida && 'rotate-180')} />
            <span className={cn(recolhida && 'sr-only')}>Recolher o menu</span>
          </button>
        </div>
      </div>
    </>
  )
}

/**
 * Um item do menu.
 *
 * O selo "em breve" fica dentro do link de propósito: quem usa leitor de tela ouve "Chamados, em
 * breve" e decide antes de abrir, em vez de descobrir na página.
 *
 * @param props o item, se está aceso, se o menu está recolhido e o que fazer ao navegar
 * @returns o link do item
 */
function ItemDoMenu({
  item,
  ativo,
  recolhida,
  aoNavegar,
}: {
  item: ItemDeNavegacao
  ativo: boolean
  recolhida: boolean
  aoNavegar: () => void
}) {
  const Icone = item.icone
  return (
    <Link
      to={item.para}
      onClick={aoNavegar}
      aria-current={ativo ? 'page' : undefined}
      // O resumo em linguagem de leigo, escrito para cada item, vira a dica do link: é a única
      // forma de quem lê "IAD" ou "IMR" saber o que é antes de entrar. Recolhido, o rótulo vai junto.
      title={recolhida ? `${item.rotulo} — ${item.resumo}` : item.resumo}
      aria-description={item.resumo}
      className={cn(
        'flex items-center gap-2.5 rounded-r-lg border-l-4 py-1.5 pl-3 pr-2 text-sm transition-colors',
        recolhida && 'lg:justify-center',
        ativo
          ? 'border-laranja-500 bg-niteroi-50 font-semibold text-niteroi-800'
          : 'border-transparent font-medium text-slate-700 hover:bg-slate-100 hover:text-slate-900',
      )}
    >
      <Icone className={cn('h-5 w-5', ativo ? 'text-niteroi-700' : 'text-slate-500')} />
      <span className={cn('leading-tight', recolhida && 'lg:sr-only')}>{item.rotulo}</span>
      {item.situacao === 'em-construcao' && (
        <span
          className={cn(
            'ml-auto shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600',
            recolhida && 'lg:hidden',
          )}
        >
          em breve
        </span>
      )}
    </Link>
  )
}

/** Tab circula dentro da gaveta em vez de vazar para a página atrás dela. */
function prenderFoco(evento: KeyboardEvent, caixa: HTMLElement | null): void {
  const alvos = Array.from(caixa?.querySelectorAll<HTMLElement>(FOCAVEIS) ?? [])
  const primeiro = alvos[0]
  const ultimo = alvos[alvos.length - 1]
  if (!primeiro || !ultimo) {
    return
  }
  if (evento.shiftKey && document.activeElement === primeiro) {
    evento.preventDefault()
    ultimo.focus()
  } else if (!evento.shiftKey && document.activeElement === ultimo) {
    evento.preventDefault()
    primeiro.focus()
  }
}
